import { zodResolver } from "@hookform/resolvers/zod";
import merge from "lodash.merge";
import { Col, Row } from "react-bootstrap";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { ProposedProjectController } from "services/proposedProject/controller";
import {
  Checkbox,
  DatePicker,
  Dropdown,
  FilePicker,
  Textarea,
  useAccordionWizard,
} from "stories";
import { AccordionForm } from "stories/components/accordionForm";
import { useFileUpload } from "utils/customHooks/useFileUpload";
import { useToast } from "utils/customHooks/useToast";
import { z } from "zod";
import { staticEntities } from "../constants";

const proposedProjectController = ProposedProjectController();

const getReportModel = () => ({ filename: "", synopsis: "" });
const getEnvironmentalReportModel = () => ({
  environmental_report_available: false,
  environmental_reports: [null, null, null],
  expected_obtaining_date: null,
});

const schema = z
  .object({
    environmental_report_available: z.boolean().nullable(),
    environmental_reports: z.any(),
    expected_obtaining_date: z.any(),
  })
  .refine(
    (data) =>
      data.environmental_report_available ||
      (!data.environmental_report_available &&
        typeof data.expected_obtaining_date === "string" &&
        data.expected_obtaining_date.length > 0),
    {
      message: "This field is required",
      path: ["expected_obtaining_date"],
    }
  );

export const EnvironmentalReportQueryKey = "environmental-report";
const EnvironmentalReport = () => {
  const {
    formState: { errors },
    ...form
  } = useForm({
    defaultValues: getEnvironmentalReportModel(),
    resolver: zodResolver(schema),
  });
  const params = useParams();
  const queryKey = [EnvironmentalReportQueryKey, { id: params.id }];
  const { next } = useAccordionWizard();
  const { showToast } = useToast();
  const envReportAvailable = useWatch({
    control: form.control,
    name: "environmental_report_available",
  });
  const queryClient = useQueryClient();
  const { handleFileFields } = useFileUpload();
  const handleUpdate = async (values) => {
    return handleFileFields(values).then((values) => {
      return proposedProjectController.updateEnvironmentalReport({
        ...values,
        opportunityId: params.id,
      });
    });
  };

  const { isLoading: fetchLoading } = useQuery({
    queryKey,
    queryFn: proposedProjectController.getEnvironmentalReport.bind(
      null,
      params.id
    ),
    onSuccess: (data) => {
      if (data.environmental_reports === null)
        delete data.environmental_reports;
      form.reset(merge(getEnvironmentalReportModel(), data));
    },
  });

  const { mutate, isLoading: postLoading } = useMutation(handleUpdate, {
    onSuccess: () => {
      showToast("Environmental report updated!", "success");
      next();
      queryClient.invalidateQueries(queryKey);
    },
  });

  const [phase1, phase2, phase3] = useWatch({
    control: form.control,
    name: [
      "environmental_reports[0]",
      "environmental_reports[1]",
      "environmental_reports[2]",
    ],
  });
  return (
    <AccordionForm
      form={form}
      accordionKey="environmental-report"
      accordionTitle="Environmental Report"
      isPostLoading={postLoading}
      isPreLoading={fetchLoading}
      onSubmit={mutate}
    >
      <Row>
        <Col xs={4}>
          <Controller
            name="environmental_report_available"
            control={form.control}
            render={({ field }) => (
              <Dropdown
                label={"Environmental Report Available?"}
                list={staticEntities.booleanList}
                fullWidth
                {...field}
                onChange={(e) => {
                  e.target.value = e.target.value === "true";
                  field.onChange(e);
                }}
                value={String(field.value)}
              />
            )}
          />
        </Col>
      </Row>
      {envReportAvailable && (
        <>
          <Row>
            <Col xs={6}>
              <Checkbox
                label={"Phase 1"}
                onChange={() => {
                  const reports = form.getValues().environmental_reports;
                  if (reports[0]) {
                    reports[0] = null;
                  } else {
                    reports[0] = getReportModel();
                  }
                  form.setValue("environmental_reports", reports);
                }}
                checked={!!phase1}
              />
            </Col>
          </Row>
          {phase1 && (
            <Row>
              <Col xs={6}>
                <Textarea
                  label={"Report Synopsis"}
                  {...form.register("environmental_reports[0].synopsis")}
                />
              </Col>
              <Col xs={6}>
                <Controller
                  control={form.control}
                  name={`environmental_reports[0].filename`}
                  render={({ field }) => (
                    <FilePicker
                      multiple={false}
                      type="file"
                      label={"Environmental Report"}
                      {...field}
                    />
                  )}
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col xs={6}>
              <Checkbox
                label={"Phase 2"}
                onChange={() => {
                  const reports = form.getValues().environmental_reports;
                  if (reports[1]) {
                    reports[1] = null;
                  } else {
                    reports[1] = getReportModel();
                  }
                  form.setValue("environmental_reports", reports);
                }}
                checked={!!phase2}
              />
            </Col>
          </Row>
          {phase2 && (
            <Row>
              <Col xs={6}>
                <Textarea
                  label={"Report Synopsis"}
                  {...form.register("environmental_reports[1].synopsis")}
                />
              </Col>
              <Col xs={6}>
                <Controller
                  control={form.control}
                  name={`environmental_reports[1].filename`}
                  render={({ field }) => (
                    <FilePicker
                      multiple={false}
                      type="file"
                      label={"Environmental Report"}
                      {...field}
                    />
                  )}
                />
              </Col>
            </Row>
          )}

          <Row>
            <Col xs={6}>
              <Checkbox
                label={"Phase 3"}
                onChange={() => {
                  const reports = form.getValues().environmental_reports;
                  if (reports[2]) {
                    reports[2] = null;
                  } else {
                    reports[2] = getReportModel();
                  }
                  form.setValue("environmental_reports", reports);
                }}
                checked={!!phase3}
              />
            </Col>
          </Row>
          {phase3 && (
            <Row>
              <Col xs={6}>
                <Textarea
                  label={"Report Synopsis"}
                  {...form.register("environmental_reports[2].synopsis")}
                />
              </Col>
              <Col xs={6}>
                <Controller
                  control={form.control}
                  name={`environmental_reports[2].filename`}
                  render={({ field }) => (
                    <FilePicker
                      multiple={false}
                      type="file"
                      label={"Environmental Report"}
                      {...field}
                    />
                  )}
                />
              </Col>
            </Row>
          )}
        </>
      )}
      {!envReportAvailable && (
        <Row>
          <Col xs={4}>
            <Controller
              control={form.control}
              name={`expected_obtaining_date`}
              render={({ field }) => {
                return (
                  <DatePicker
                    label="Expected Obtaining Date"
                    error={errors?.expected_obtaining_date?.message}
                    {...field}
                  />
                );
              }}
            />
          </Col>
        </Row>
      )}
    </AccordionForm>
  );
};

export default EnvironmentalReport;
